import React, { useEffect, useState } from "react";
import Modal from "../modals/modal/Modal";
// Same popup shell, same warning colours as TrialBanner — this is the same class of message in
// the same slot, so it reuses the styles rather than declaring near-copies.
import styles from "../TrialBanner/TrialBanner.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { isAuthSelector } from "../../services/selectors/auth.selectors";
import { useListSeatSubscriptionsQuery } from "../../services/api/featureCreditPackagesApi";
import {
  earliestGraceEndsAt,
  pastDueRows,
  seatsAtRisk,
} from "../../utils/seatSubscriptions";
import { isAdminRole } from "../../utils/featureCreditsAuth";
import { clearCardUpdatedForRetry, wasCardUpdatedForRetry } from "../../utils/billingRecovery";
import { formatDateInIsrael } from "../../utils/utils";
import doctor from "../../images/questionnaire_doctor.png";

// App-level rather than confined to the seat-packs tab, because the remedy window is the one day
// between the failed charge and the next retry, and a banner inside the tab is only seen by someone
// who already went looking.
const SeatPackPastDueBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const admin = useAppSelector(adminSelector);
  const isAuth = useAppSelector(isAuthSelector);

  // Skipped for non-admins because the endpoint 403s for them — otherwise every therapist and
  // patient pays for a guaranteed failure on each page load.
  const { data: rows = [], isSuccess } = useListSeatSubscriptionsQuery(undefined, {
    skip: !isAuth || !isAdminRole(admin.roleId),
  });

  const pastDueCount = pastDueRows(rows).length;

  // Rows coming back clean means the retry landed, so the "we'll retry" reassurance has served its
  // purpose and must not greet the user again on the next past-due charge.
  //
  // Gated on isSuccess, not just an empty list: before the first response there are no rows either,
  // and clearing then would wipe the flag on the very page load after the card was entered — the
  // one moment it exists for.
  useEffect(() => {
    if (isSuccess && pastDueCount === 0) clearCardUpdatedForRetry();
  }, [isSuccess, pastDueCount]);

  if (dismissed || pastDueCount === 0) return null;

  const seats = seatsAtRisk(rows);
  const graceEndsAt = earliestGraceEndsAt(rows);

  // Two states, correct whether or not a card update re-charges immediately. Under an
  // immediate-retry backend the rows come back Active and this banner unmounts on its own, so the
  // second state is never seen; under a nightly-job backend it is what stops the unchanged alarm
  // from inviting a second card entry.
  //
  // Read from sessionStorage during render rather than held in state: the flip happens when the
  // card change invalidates these rows and re-renders this component, which is exactly when the
  // fresh value is wanted.
  const message = wasCardUpdatedForRetry()
    ? t("SEAT_PACKS_CARD_UPDATED_RETRY_PENDING", { count: seats })
    : graceEndsAt
      ? t("SEAT_PACKS_PAST_DUE_BANNER", { count: seats, date: formatDateInIsrael(graceEndsAt) })
      : t("SEAT_PACKS_PAST_DUE_BANNER_IMMINENT", { count: seats });

  // Component state only, never sessionStorage/localStorage: a banner that stays dismissed defeats
  // a one-day deadline, and one that can't be cleared while the user works is hostile. It comes
  // back on the next load.
  return (
    <Modal onClose={() => setDismissed(true)}>
      <div role="alert">
        <img
          src={doctor}
          alt={t('IMAGE_OF_VENUS')}
          className={styles.popup__img}
        />

        <p className={`${styles.message} ${styles.messageWarning}`}>{message}</p>
        <button
          type="button"
          className={`button hover ${styles.actionButton}`}
          onClick={() => {
            setDismissed(true);
            navigate("/myAccount?tab=payment");
          }}
        >
          {t("UPDATE_CARD")}
        </button>
      </div>
    </Modal>
  );
};

export default SeatPackPastDueBanner;
